import { useCallback } from 'react'

import { useMutation as useRelayMutation, UseMutationConfig } from 'react-relay'

import {
  GraphQLTaggedNode,
  MutationParameters,
  PayloadError,
} from 'relay-runtime'

import { addBreadcrumb, captureException } from '@sentry/react'

export const useMutation = <TMutation extends MutationParameters>(
  mutationGraphQL: GraphQLTaggedNode
) => {
  const [mutation] = useRelayMutation<TMutation>(mutationGraphQL)

  return useCallback(
    (config: UseMutationConfig<TMutation>) =>
      new Promise<TMutation['response']>((resolve, reject) => {
        mutation({
          ...config,
          onCompleted: (response, errors) => {
            try {
              if (config.onCompleted) {
                config.onCompleted(response, errors)
              }
            } finally {
              if (errors) {
                reject(new GraphQLError(errors))

                addBreadcrumb({
                  category: 'graphql.error',
                  message: JSON.stringify(errors),
                  level: 'debug',
                })
              } else {
                resolve(response)
              }
            }
          },
          onError: (error) => {
            try {
              if (config.onError) {
                config.onError(error)
              }
            } finally {
              reject(error)

              captureException(error)
            }
          },
        })
      }),
    [mutation]
  )
}

export class GraphQLError extends Error {
  constructor(errors: PayloadError[]) {
    const message = JSON.stringify(errors)

    super(message)

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, GraphQLError)
    }

    this.name = 'GraphQLError'
    this.message = message
    this.errors = errors
  }

  errors: PayloadError[]
}

/**
 * The server's own explanation for a rejected mutation.
 *
 * Resolvers refuse orders and other actions with specific, actionable messages
 * ("Only 2 tickets remaining for tier ..."). Replacing those with a generic
 * "something went wrong" string leaves the user with no idea what to change and
 * leaves us with nothing to debug from a screenshot, so prefer this and fall
 * back to the generic copy only when the failure carries no message.
 */
export const graphQLErrorMessage = (error: unknown): string | undefined => {
  if (!(error instanceof GraphQLError)) return undefined

  const message = error.errors?.[0]?.message?.trim()

  return message || undefined
}
